/*  ========================================================================
    # Sections - Slider
    ========================================================================  */

import React, { useState, type CSSProperties } from 'react';
import type { SliderProps } from '.';
import { useSwipeable } from 'react-swipeable';
import clsx from 'clsx';
import useSVGSprite from '@/react/hooks/useSVGSprite';

const Slider = ({ headline, content, entries }: SliderProps) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const svgSprite = useSVGSprite();

  /**
   * Handle slide to left.
   */
  const slideLeft = () => {
    if (activeSlide !== 0) {
      setActiveSlide(prev => prev - 1);
    } else {
      setActiveSlide(entries.length - 1);
    }
  };

  /**
   * Handle slide to right.
   */
  const slideRight = () => {
    if (activeSlide + 1 < entries.length) {
      setActiveSlide(prev => prev + 1);
    } else {
      setActiveSlide(0);
    }
  };

  /**
   * Add swipe handlers.
   */
  const swipeHandlers = useSwipeable({
    onSwipedLeft: slideRight,
    onSwipedRight: slideLeft
  });

  return (
    <div className='container'>
      {headline !== '' ? (
        <div className='w-full xl:w-10/12 xxl:w-8/12 mx-auto'>
          <div className='prose mb-14 text-center' dangerouslySetInnerHTML={{ __html: headline }} />
        </div>
      ) : null}

      <div className='row gap-y-8'>
        <div className='col w-full xl:w-4/12'>
          <div className='s-slider__content-wrapper'>
            <div className='s-slider__content'>
              <div className='prose prose-white' dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          </div>
        </div>
        <div className='col w-full xl:w-8/12'>
          <div
            className='s-slider__slider'
            style={
              { '--active-slide': activeSlide } as CSSProperties & {
                '--active-slide': number;
              }
            }
            {...swipeHandlers}
          >
            <div
              className='s-slider__slider-axis'
              style={{ transform: 'translateX(calc((-100% / 1 * var(--active-slide)))' }}
            >
              {entries.map((entry, index) => (

                <div
                  key={`entry-${index}`}
                  className={clsx(
                    's-slider__slider-entry',
                    index === activeSlide ? 's-slider__slider-entry--active' : ''
                  )}
                >
                  {entry.type === 'image' ? (
                    <figure className='s-slider__slider-entry-image'>
                      <img src={entry.image.sizes['1536x1536']} alt={entry.image.alt} />
                    </figure>
                  ) : (
                    <div className='s-slider__slider-entry-content'>
                      <div className='prose' dangerouslySetInnerHTML={{ __html: entry.content }} />
                    </div>
                  )}
                </div>

              ))}
            </div>
          </div>
        </div>
        {entries.length > 1 ? (
          <div className='col w-full'>
            <div className='s-slider__slider-controls'>
              <button
                onClick={slideLeft}
              >
                <svg style={{ transform: 'translateX(-1px)' }}>
                  <use href={`${svgSprite}#chevron-left`} />
                </svg>
              </button>
              {entries.map((entry, index) => (
                <button key={`dot-${index}`} style={{ backgroundColor: index === activeSlide ? '#ff4400' : '#CED0CE' }} onClick={() => setActiveSlide(index)} />
              ))}
              <button
                onClick={slideRight}
              >
                <svg style={{ transform: 'translateX(1px)' }}>
                  <use href={`${svgSprite}#chevron-right`} />
                </svg>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Slider;
